import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { formatTaxIdInput } from "../lib/tax-id";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DynamicAddressList,
  FieldRow,
  UnderlineInput,
  MAX_ADDRESS_ROWS,
  emptyAddressRows,
  normalizeAddressRows,
} from "../components/service-order/AddressFields";
import { ClipboardList, Plus, ArrowLeft, Download, CheckCircle2, Clock, History } from "lucide-react";
import { toast } from "sonner";

const TEAL = "#0d9488";
const MIN_COLETA_ROWS = 3;
const MIN_ENTREGA_ROWS = 1;

const EMPTY_FORM_BASE = {
  numero_os: "",
  data_horario_serv: "",
  cliente_pagador: "",
  cliente_tax_id: "",
  mercadoria: "",
  km_inicial: "",
  km_final: "",
  km_total: "",
  valor_frete: "",
  placa_veiculo: "",
  nome_motorista: "",
  observacoes: "",
  deliverer_id: "",
};

function emptyForm() {
  return {
    ...EMPTY_FORM_BASE,
    enderecos_coleta: emptyAddressRows(MIN_COLETA_ROWS),
    enderecos_entrega: emptyAddressRows(MIN_ENTREGA_ROWS),
  };
}

const STATUS_CLASS = {
  pendente_aprovacao: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  aprovada: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  cancelada: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const FREIGHT_FIXED = 20;
const FREIGHT_PER_KM = 2.5;

function calcKmTotal(kmInicial, kmFinal) {
  const a = parseFloat(kmInicial);
  const b = parseFloat(kmFinal);
  if (Number.isNaN(a) || Number.isNaN(b)) return "";
  const total = b - a;
  return total >= 0 ? String(total) : "";
}

function calcValorFrete(kmTotal) {
  const km = parseFloat(kmTotal);
  if (Number.isNaN(km) || km < 0) return "";
  return (FREIGHT_FIXED + km * FREIGHT_PER_KM).toFixed(2);
}

function orderToForm(data, { includeFreight = true } = {}) {
  const kmTotal = data.km_total != null ? String(data.km_total) : "";
  return {
    ...emptyForm(),
    numero_os: data.numero_os || "",
    data_horario_serv: toDatetimeLocal(data.data_horario_serv),
    cliente_pagador: data.cliente_pagador || "",
    cliente_tax_id: data.recipient_cpf_cliente || data.recipient_cnpj_cliente || "",
    enderecos_coleta: normalizeAddressRows(
      data.enderecos_coleta,
      [data.endereco_coleta01, data.endereco_coleta02, data.endereco_coleta03],
      MIN_COLETA_ROWS,
    ),
    mercadoria: data.mercadoria || "",
    enderecos_entrega: normalizeAddressRows(
      data.enderecos_entrega,
      [data.endereco_entrega],
      MIN_ENTREGA_ROWS,
    ),
    km_inicial: data.km_inicial != null ? String(data.km_inicial) : "",
    km_final: data.km_final != null ? String(data.km_final) : "",
    km_total: kmTotal,
    valor_frete: includeFreight
      ? (data.valor_frete != null && data.valor_frete !== ""
        ? Number(data.valor_frete).toFixed(2)
        : calcValorFrete(kmTotal))
      : "",
    placa_veiculo: data.placa_veiculo || "",
    nome_motorista: data.nome_motorista || "",
    observacoes: data.observacoes || "",
    deliverer_id: data.deliverer_id || "",
  };
}

function SectionHeader({ children }) {
  return (
    <div
      className="text-white text-sm font-semibold px-4 py-2 rounded-t-md"
      style={{ backgroundColor: TEAL }}
    >
      {children}
    </div>
  );
}

function StatusBadge({ status, t }) {
  const key = `so_status_${status || "pendente_aprovacao"}`;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_CLASS[status] || STATUS_CLASS.pendente_aprovacao}`}>
      {status === "aprovada" ? <CheckCircle2 size={12} /> : <Clock size={12} />}
      {t(key)}
    </span>
  );
}

export default function ServiceOrder() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isDeliverer = user?.role === "deliverer";
  const isAdmin = user?.role === "admin" || user?.role === "operator";
  const isNew = !id || id === "new";

  const [orders, setOrders] = useState([]);
  const [deliverers, setDeliverers] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [exporting, setExporting] = useState(null);

  const status = currentOrder?.status || (isNew ? null : "pendente_aprovacao");
  const isReadOnly = !isNew && (isDeliverer || status === "aprovada" || status === "cancelada");
  const canAdminEdit = isAdmin && status === "pendente_aprovacao" && !isNew;
  const canApprove = canAdminEdit;
  const canEditFreight = user?.role === "admin" && !isReadOnly;

  const loadOrders = useCallback(async () => {
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const { data } = await api.get("/service-orders", { params });
      setOrders(data || []);
    } catch {
      /* offline */
    }
  }, [statusFilter]);

  const loadOrder = useCallback(async (orderId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/service-orders/${orderId}`);
      setCurrentOrder(data);
      setForm(orderToForm(data, { includeFreight: isAdmin }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/users?role=deliverer").then((r) => setDeliverers(r.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    if (id && id !== "new") loadOrder(id);
    else {
      setCurrentOrder(null);
      setForm(emptyForm());
    }
  }, [id, loadOrder]);

  const setField = (key, value) => {
    if (isReadOnly) return;
    if (key === "valor_frete" && !canEditFreight) return;
    setForm((prev) => {
      const nextValue = key === "cliente_tax_id" ? formatTaxIdInput(value) : value;
      const next = { ...prev, [key]: nextValue };
      if (key === "km_inicial" || key === "km_final") {
        const kmInicial = key === "km_inicial" ? value : prev.km_inicial;
        const kmFinal = key === "km_final" ? value : prev.km_final;
        next.km_total = calcKmTotal(kmInicial, kmFinal);
        if (canEditFreight) {
          const previousFormula = calcValorFrete(prev.km_total);
          const current = String(prev.valor_frete ?? "");
          if (current === "" || current === previousFormula) {
            next.valor_frete = calcValorFrete(next.km_total);
          }
        }
      }
      return next;
    });
  };

  const setAddressList = (key, next) => {
    if (isReadOnly) return;
    setForm((prev) => ({ ...prev, [key]: next }));
  };

  const payload = useMemo(() => ({
    data_horario_serv: form.data_horario_serv || null,
    cliente_pagador: form.cliente_pagador || null,
    cliente_tax_id: form.cliente_tax_id || undefined,
    enderecos_coleta: form.enderecos_coleta,
    endereco_coleta01: form.enderecos_coleta?.[0] || null,
    endereco_coleta02: form.enderecos_coleta?.[1] || null,
    endereco_coleta03: form.enderecos_coleta?.[2] || null,
    mercadoria: form.mercadoria || null,
    enderecos_entrega: form.enderecos_entrega,
    endereco_entrega: form.enderecos_entrega?.[0] || null,
    km_inicial: form.km_inicial !== "" ? Number(form.km_inicial) : null,
    km_final: form.km_final !== "" ? Number(form.km_final) : null,
    km_total: form.km_total !== "" ? Number(form.km_total) : null,
    placa_veiculo: form.placa_veiculo || null,
    nome_motorista: form.nome_motorista || null,
    observacoes: form.observacoes || null,
    ...(isAdmin ? { deliverer_id: form.deliverer_id || null } : {}),
    ...(canEditFreight && form.valor_frete !== "" && Number.isFinite(Number(form.valor_frete))
      ? { valor_frete: Math.round(Number(form.valor_frete) * 100) / 100 }
      : {}),
  }), [form, isAdmin, canEditFreight]);

  const submit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    setSaving(true);
    try {
      if (!isNew) {
        await api.patch(`/service-orders/${id}`, payload);
        toast.success(t("so_updated"));
        loadOrder(id);
      } else {
        const { data } = await api.post("/service-orders", payload);
        toast.success(isDeliverer ? t("so_created_pending") : t("so_created"));
        navigate(`/service-orders/${data.id}`, { replace: true });
      }
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    const valor = parseFloat(form.valor_frete);
    if (!Number.isFinite(valor) || valor <= 0) {
      toast.error(t("so_frete_required"));
      return;
    }
    setApproving(true);
    try {
      const { data } = await api.post(`/service-orders/${id}/approve`, { valor_frete: valor });
      toast.success(t("so_approved"));
      setCurrentOrder(data);
      setForm(orderToForm(data, { includeFreight: true }));
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setApproving(false);
    }
  };

  const exportOrders = async (fmt) => {
    setExporting(fmt);
    try {
      if (fmt === "json") {
        const { data } = await api.get("/reports/service_orders.json");
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "nafe_ordens_servico.json";
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const res = await api.get(`/reports/service_orders.${fmt}`, { responseType: "blob" });
        const types = {
          csv: "text/csv",
          xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          pdf: "application/pdf",
        };
        const blob = new Blob([res.data], { type: types[fmt] || "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `nafe_ordens_servico.${fmt}`;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast.success(t("so_export_ok"));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setExporting(null);
    }
  };

  const newOrder = () => {
    setCurrentOrder(null);
    setForm(emptyForm());
    navigate("/service-orders/new");
  };

  const ro = isReadOnly;

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">{t("loading")}</div>;
  }

  return (
    <div className="space-y-4 max-w-5xl mx-auto" data-testid="service-order-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl flex items-center gap-2">
            <ClipboardList size={22} /> {t("nav_service_orders")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("so_subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isNew && status && <StatusBadge status={status} t={t} />}
          {(isNew || !isDeliverer) && (
            <Button variant="outline" size="sm" onClick={newOrder}>
              <Plus size={14} className="mr-1" /> {t("so_new")}
            </Button>
          )}
        </div>
      </div>

      {isDeliverer && !isNew && status === "pendente_aprovacao" && (
        <Card className="p-4 border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
          <p className="text-sm text-amber-900 dark:text-amber-200">{t("so_deliverer_pending_msg")}</p>
        </Card>
      )}

      {isAdmin && (
        <Card className="p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold flex items-center gap-2"><History size={16} /> {t("so_history_title")}</h2>
            <div className="flex flex-wrap gap-2">
              {["json", "csv", "xlsx", "pdf"].map((fmt) => (
                <Button key={fmt} size="sm" variant="outline" disabled={!!exporting} onClick={() => exportOrders(fmt)}>
                  <Download size={12} className="mr-1" />
                  {exporting === fmt ? "..." : fmt.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("so_filter_all")}</SelectItem>
              <SelectItem value="pendente_aprovacao">{t("so_status_pendente_aprovacao")}</SelectItem>
              <SelectItem value="aprovada">{t("so_status_aprovada")}</SelectItem>
            </SelectContent>
          </Select>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-3">OS</th>
                  <th className="py-2 pr-3">{t("so_cliente_pagador")}</th>
                  <th className="py-2 pr-3">{t("so_valor_frete")}</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2">{t("so_datetime")}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    className={`border-b cursor-pointer hover:bg-muted/50 ${o.id === id ? "bg-muted/70" : ""}`}
                    onClick={() => navigate(`/service-orders/${o.id}`)}
                  >
                    <td className="py-2 pr-3 font-mono font-bold text-red-600">{o.numero_os}</td>
                    <td className="py-2 pr-3">{o.cliente_pagador || "—"}</td>
                    <td className="py-2 pr-3">{o.valor_frete != null ? `R$ ${Number(o.valor_frete).toFixed(2)}` : "—"}</td>
                    <td className="py-2 pr-3"><StatusBadge status={o.status} t={t} /></td>
                    <td className="py-2">{o.data_horario_serv ? new Date(o.data_horario_serv).toLocaleString("pt-BR") : "—"}</td>
                  </tr>
                ))}
                {!orders.length && (
                  <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">{t("so_history_empty")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <form onSubmit={submit} className="bg-white dark:bg-[var(--surface)] border rounded-lg overflow-hidden shadow-sm">
        <div className="text-white px-6 py-3 text-center font-semibold text-lg" style={{ backgroundColor: TEAL }}>
          {t("so_title_transport")}
        </div>

        {/* Dados da Ordem de Serviço */}
        <div className="px-6 py-2 bg-slate-100 dark:bg-[var(--surface-2)]">
          <SectionHeader>{t("so_section_order")}</SectionHeader>
          <div className="bg-white dark:bg-[var(--surface)] px-4 py-2 rounded-b-md border border-t-0" style={{ borderColor: TEAL }}>
            <FieldRow label={t("so_numero_os")}>
              <Input 
                value={form.numero_os} 
                readOnly 
                placeholder={t("so_numero_os_auto")} 
                className="font-mono font-bold text-red-600"
              />
            </FieldRow>
            <FieldRow label={t("so_datetime")}>
              <Input 
                type="datetime-local" 
                value={form.data_horario_serv} 
                onChange={(e) => setField("data_horario_serv", e.target.value)} 
                required 
                readOnly={ro}
              />
            </FieldRow>
            <FieldRow label={t("so_cliente_pagador")}>
              <Input 
                value={form.cliente_pagador} 
                onChange={(e) => setField("cliente_pagador", e.target.value)} 
                required 
                readOnly={ro}
              />
            </FieldRow>
            <FieldRow label={t("so_cliente_tax_id")}>
              <Input 
                value={form.cliente_tax_id} 
                onChange={(e) => setField("cliente_tax_id", e.target.value)} 
                placeholder="000.000.000-00" 
                inputMode="numeric" 
                maxLength={18} 
                readOnly={ro}
              />
            </FieldRow>
            {isAdmin && (
              <FieldRow label={t("so_deliverer")}>
                <Select value={form.deliverer_id || "none"} onValueChange={(v) => setField("deliverer_id", v === "none" ? "" : v)} disabled={ro}>
                  <SelectTrigger className="max-w-xs">
                    <SelectValue placeholder={t("so_deliverer_select")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("so_deliverer_none")}</SelectItem>
                    {deliverers.map((d) => 
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="border-2 rounded-md overflow-hidden" style={{ borderColor: TEAL }}>
            <SectionHeader>{t("so_section_service")}</SectionHeader>
            <div className="px-4 py-3 space-y-1">
              <DynamicAddressList
                values={form.enderecos_coleta}
                onChange={(next) => setAddressList("enderecos_coleta", next)}
                labelTemplate={t("so_coleta")}
                addLabel={t("so_add_coleta")}
                removeLabel={t("so_remove_coleta")}
                readOnly={ro}
                minRows={MIN_COLETA_ROWS}
                maxRows={MAX_ADDRESS_ROWS}
                accentColor={TEAL}
                testIdPrefix="so-coleta"
              />
              <FieldRow label={t("so_mercadoria")}>
                <UnderlineInput value={form.mercadoria} onChange={(e) => setField("mercadoria", e.target.value)} readOnly={ro} />
              </FieldRow>
              <DynamicAddressList
                values={form.enderecos_entrega}
                onChange={(next) => setAddressList("enderecos_entrega", next)}
                labelTemplate={t("so_entrega")}
                addLabel={t("so_add_entrega")}
                removeLabel={t("so_remove_entrega")}
                readOnly={ro}
                minRows={MIN_ENTREGA_ROWS}
                maxRows={MAX_ADDRESS_ROWS}
                accentColor={TEAL}
                testIdPrefix="so-entrega"
              />
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                {["km_inicial", "km_final", "km_total"].map((k) => (
                  <FieldRow key={k} label={t(`so_${k}`)} className="flex-1">
                    <UnderlineInput type={k === "km_total" ? "text" : "number"} min="0" step="0.1" value={form[k]} onChange={(e) => setField(k, e.target.value)} readOnly={ro || k === "km_total"} />
                  </FieldRow>
                ))}
              </div>
            </div>

            <SectionHeader>{t("so_section_complement")}</SectionHeader>
            <div className="px-4 py-3 space-y-1">
              {isAdmin && (
                <FieldRow label={t("so_valor_frete")}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500 shrink-0">R$</span>
                      <UnderlineInput
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={form.valor_frete}
                        onChange={(e) => setField("valor_frete", e.target.value)}
                        readOnly={!canEditFreight}
                        placeholder={canEditFreight ? "0.00" : t("so_valor_frete_admin")}
                        data-testid="so-valor-frete"
                      />
                    </div>
                    {!canEditFreight && !ro && (
                      <p className="text-xs text-muted-foreground mt-1">{t("so_valor_frete_only_admin")}</p>
                    )}
                  </div>
                </FieldRow>
              )}
              <div className="flex flex-col sm:flex-row gap-4">
                <FieldRow label={t("so_placa")} className="flex-1">
                  <UnderlineInput value={form.placa_veiculo} onChange={(e) => setField("placa_veiculo", e.target.value)} readOnly={ro} />
                </FieldRow>
                <FieldRow label={t("so_motorista")} className="flex-1">
                  <UnderlineInput value={form.nome_motorista} onChange={(e) => setField("nome_motorista", e.target.value)} readOnly={ro} />
                </FieldRow>
              </div>
            </div>

              {/* Observações */}
              <SectionHeader>{t("so_observacoes")}</SectionHeader>
              <div className="px-4 py-3">
                <Textarea 
                  value={form.observacoes} 
                  onChange={(e) => setField("observacoes", e.target.value)} 
                  rows={7} 
                  className="resize-none"
                  style={{
                    backgroundImage: "repeating-linear-gradient(transparent, transparent 31px, #cbd5e1 31px, #cbd5e1 32px)",
                    backgroundAttachment: "local",
                  }}
                  readOnly={ro} 
                />
              </div>
          </div>
        </div>

        {currentOrder?.history?.length > 0 && (
          <div className="px-6 pb-4">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><History size={14} /> {t("so_timeline")}</h3>
            <ul className="text-xs space-y-1 text-muted-foreground">
              {[...(currentOrder.history || [])].reverse().map((h, i) => (
                <li key={i}>
                  {new Date(h.at).toLocaleString("pt-BR")} — {t(`so_action_${h.action}`) || h.action}
                  {isAdmin && h.valor_frete != null ? ` · R$ ${Number(h.valor_frete).toFixed(2)}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="px-6 py-4 flex flex-wrap gap-3 border-t bg-slate-50 dark:bg-[var(--surface-2)]">
          {!isReadOnly && (
            <Button type="submit" disabled={saving} style={{ backgroundColor: TEAL }}>
              {saving ? t("saving") : isNew ? t("so_save") : t("so_save")}
            </Button>
          )}
          {canApprove && (
            <Button type="button" disabled={approving} onClick={approve} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle2 size={14} className="mr-1" />
              {approving ? t("saving") : t("so_approve")}
            </Button>
          )}
          {!isNew && !isDeliverer && (
            <Button type="button" variant="outline" onClick={newOrder}>
              <ArrowLeft size={14} className="mr-1" /> {t("so_new")}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
